import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface PersonalStreakData {
  personalCurrentStreak: number;
  personalLongestStreak: number;
  firstActivityDate: Date | null;
  lastDrinkDate: Date | null;
}

/**
 * Calculates personal alcohol-free streak independent of any challenge/group.
 * This is the user's global sobriety timeline that continues indefinitely.
 * 
 * - Fetches ALL drink_logs for the user (ignores group_id)
 * - Calculates streak from most recent drink to today
 * - If no drinks exist, uses earliest challenge start as baseline (fallback to profile creation)
 */
export function usePersonalStreak() {
  const { user, profile } = useAuth();

  return useQuery({
    queryKey: ['personal-streak', user?.id],
    queryFn: async (): Promise<PersonalStreakData> => {
      if (!user) {
        return {
          personalCurrentStreak: 0,
          personalLongestStreak: 0,
          firstActivityDate: null,
          lastDrinkDate: null,
        };
      }

      // Helper: normalize date to midnight for consistent calendar day calculations
      const normalizeToMidnight = (date: Date): Date => {
        const normalized = new Date(date);
        normalized.setHours(0, 0, 0, 0);
        return normalized;
      };

      // Helper: calculate full calendar days between two normalized dates
      const daysBetween = (start: Date, end: Date): number => {
        return Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      };

      const today = normalizeToMidnight(new Date());

      // Determine the personal tracking baseline date:
      // Use the earliest-ever challenge start date the user participated in.
      // Fallback to profile creation date if the user has never joined a challenge.
      const profileCreatedAt = normalizeToMidnight(
        profile?.created_at ? new Date(profile.created_at) : new Date()
      );

      const [{ data: allDrinkLogs, error: drinkLogsError }, { data: earliestMembership, error: membershipError }] =
        await Promise.all([
          supabase
            .from('drink_logs')
            .select('logged_at, group_id')
            .eq('user_id', user.id)
            .order('logged_at', { ascending: true }),
          supabase
            .from('memberships')
            .select('id, groups(challenge_start)')
            .eq('user_id', user.id)
            .order('challenge_start', { foreignTable: 'groups', ascending: true })
            .limit(1)
            .maybeSingle(),
        ]);

      if (drinkLogsError) throw drinkLogsError;
      if (membershipError) throw membershipError;

      const earliestChallengeStartRaw =
        (earliestMembership as unknown as { groups?: { challenge_start?: string } | null } | null)?.groups
          ?.challenge_start ?? null;

      const earliestChallengeStart = earliestChallengeStartRaw
        ? normalizeToMidnight(new Date(earliestChallengeStartRaw))
        : null;

      const personalBaselineDate = earliestChallengeStart ?? profileCreatedAt;

      // If no drinks logged ever, streak runs from profile creation
      if (!allDrinkLogs || allDrinkLogs.length === 0) {
        const totalDays = Math.max(0, daysBetween(personalBaselineDate, today));
        return {
          personalCurrentStreak: totalDays,
          personalLongestStreak: totalDays,
          firstActivityDate: personalBaselineDate,
          lastDrinkDate: null,
        };
      }

      // Normalize all drink dates and get unique drinking days
      const drinkingDays = new Set<string>();
      for (const log of allDrinkLogs) {
        const date = normalizeToMidnight(new Date(log.logged_at));
        drinkingDays.add(date.toISOString());
      }

      // Sort unique drinking days
      const sortedDrinkDays = Array.from(drinkingDays)
        .map(d => new Date(d))
        .sort((a, b) => a.getTime() - b.getTime());

      // Calculate longest streak across all time
      let longestStreak = 0;

      // Streak from profile creation to first drink ever (days before first drink)
      const firstDrinkDay = sortedDrinkDays[0];
      const startToFirst = daysBetween(personalBaselineDate, firstDrinkDay);
      longestStreak = Math.max(longestStreak, Math.max(0, startToFirst));

      // Streaks between drinking days (gaps without alcohol)
      for (let i = 0; i < sortedDrinkDays.length - 1; i++) {
        const currentDrinkDay = sortedDrinkDays[i];
        const nextDrinkDay = sortedDrinkDays[i + 1];
        // Gap = days between drinks minus 1 (the day after current drink to day before next drink)
        const gap = daysBetween(currentDrinkDay, nextDrinkDay) - 1;
        longestStreak = Math.max(longestStreak, Math.max(0, gap));
      }

      // Current streak: full days since last drink (not counting the drinking day itself)
      const lastDrinkDay = sortedDrinkDays[sortedDrinkDays.length - 1];
      // If last drink was today, current streak is 0
      // If last drink was yesterday, current streak is 0 (today is day 1, but not complete yet)
      // Actually: streak counts complete days without alcohol
      // Day after last drink = day 1 of streak
      const daysSinceLastDrink = daysBetween(lastDrinkDay, today);
      const currentStreak = Math.max(0, daysSinceLastDrink);
      longestStreak = Math.max(longestStreak, currentStreak);

      return {
        personalCurrentStreak: currentStreak,
        personalLongestStreak: longestStreak,
        firstActivityDate: personalBaselineDate,
        lastDrinkDate: lastDrinkDay,
      };
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
